import { useParams, Link } from 'react-router-dom';
import { usePage } from '../hooks/useCMS';
import { FadeIn, LoadingSpinner } from '../components/ui/AnimatedCard';
import { ArrowLeft, AlertCircle } from 'lucide-react';

export function DynamicPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: page, isLoading, error } = usePage(slug || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <FadeIn>
        <div className="max-w-2xl mx-auto text-center py-16">
          <AlertCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Page Not Found</h1>
          <p className="text-slate-500 mb-6">The page you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Home
          </Link>
        </div>
      </FadeIn>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <FadeIn>
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
          <span>/</span>
          <span className="text-slate-900 font-medium">{page.title}</span>
        </nav>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">{page.title}</h1>
          {page.titleBn && (
            <p className="text-xl text-slate-500 font-bangla">{page.titleBn}</p>
          )}
        </div>

        {/* Page Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
          <div 
            className="prose prose-slate max-w-none"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </div>

        {/* Bengali Content if available */}
        {page.contentBn && (
          <div className="mt-6 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4 font-bangla">বাংলায় পড়ুন</h2>
            <div 
              className="prose prose-slate max-w-none font-bangla"
              dangerouslySetInnerHTML={{ __html: page.contentBn }}
            />
          </div>
        )}
      </FadeIn>
    </div>
  );
}
